import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import {
    Image2Text,
    GetSemanticId,
    type ApiGetSemanticIdResponse,
} from '@/infrastructure/api-functions/interface/ai-image.interface';
import { Message } from '@patsnap/tahiti-core/common/message';
import { HistoryService } from '@/services/history/service/history.service';

export const AiImageService = Symbol('AiImageService');

export enum TechStep {
    TECH_UPLOAD = 'TECH_UPLOAD',
    TECH_DESC = 'TECH_DESC',
    TECH_SEMANTIC = 'TECH_SEMANTIC',
}

export interface AiImageService {
    step: TechStep;
    userDesc: string;
    techDesc: string;
    isLoading: boolean;
    isGettingUrl: boolean;
    isError: boolean;
    image: any;
    updateUserDesc: (value: string) => void;
    updateImage: (value: any) => void;
    image2text: () => void;
    getUrl: () => Promise<string>;
    restart: () => void;
}

@injectable()
export class AiImageServiceImpl implements AiImageService {
    public userDesc = '';
    public techDesc = '';
    public isError = false;
    public isLoading = false;
    public isGettingUrl = false;
    public image: any = null;
    public step = TechStep.TECH_UPLOAD;

    constructor(
        @inject(Message)
        private _message: Message,
        @inject(Image2Text)
        private image2TextApi: Image2Text,
        @inject(GetSemanticId)
        private getSemanticIdApi: GetSemanticId,
        @inject(HistoryService)
        private _historyService: HistoryService,
    ) { }

    public updateUserDesc(value: string) {
        this.userDesc = value;
    }

    public updateImage(value: any) {
        this.image = value;
        if (value) {
            this.step = TechStep.TECH_DESC;
        }
    }

    public async image2text() {
        this.isLoading = true;
        this.step = TechStep.TECH_SEMANTIC;
        const formData = new FormData();
        formData.append('file', this.image);

        try {
            const taskId = this._historyService.getModelData().taskId;
            this.techDesc = await this.image2TextApi.image2text(taskId, this.userDesc, formData);

        } catch (err: any) {
            this.isError = true;
            this._message.error({
                message: err,
            });
        }

        this.isLoading = false;
    }

    private async _getSemanticId(): Promise<ApiGetSemanticIdResponse> {
        return this.getSemanticIdApi.getSemanticId({
            ipc_logic_type: 'AND',
            query: this.techDesc,
        });
    }

    public async getUrl() {
        this.isGettingUrl = true;
        const res = await this._getSemanticId();
        this.isGettingUrl = false;

        return res.url;
    }


    public restart() {
        this.step = TechStep.TECH_UPLOAD;
        this.userDesc = '';
        this.techDesc = '';
        this.image = null;
        this.isError = false;
    }
}
